<template>
	<template v-if="visible">
		<template v-if="desktop">
			<NavRail v-if="onApp" :menu="menu" :active-view="activeView" :can-hover="canHover" :version="versionLabel" :color="color" @select="openItem" @portal="openPortal" @expand="drawerOpen = true" />
			<NavDrawer v-if="onApp && !canHover" v-model="drawerOpen" :menu="menu" :active-view="activeView" :version="versionLabel" :color="color" @select="openItem" @portal="openPortal" />
			<NavTopBar v-model:panel="activePanel" @action="run" />
		</template>

		<template v-else>
			<NavBottomBar :open="mobileMenuOpen ? 'menu' : activePanel" @toggle="toggleMobile" @action="run" />
			<NavMobileMenu v-model="mobileMenuOpen" @select="openItem" @action="run" />
			<DialogControl :model-value="Boolean(activePanel)" mobile @update:model-value="onSheetToggle">
				<component :is="NAV_PANELS[sheetPanel].component" v-if="sheetPanel" @close="activePanel = null" />
			</DialogControl>
		</template>

		<NavProfileDialog v-model="dialogs.profile" />
		<NavReleaseNotesDialog v-model="dialogs.releaseNotes" />
		<NavSupportDialog v-model="dialogs.support" />
		<NavDraftCartsDialog v-model="dialogs.draftCarts" />
	</template>
	<NavImageDialog />
	<NavConfirmDialog v-model="confirm.open" :title="confirm.title" :lines="confirm.lines" :icon="confirm.icon" :confirm-text="confirm.confirmText" :cancel-text="confirm.cancelText" :input-label="confirm.inputLabel" @confirm="onConfirm" @cancel="onCancel" />
	<NavLegacyDialogs />
	<NavOfflineBlocker />
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import DialogControl from '@/components/controls/DialogControl.vue';
import NavRail from './NavRail.vue';
import NavDrawer from './NavDrawer.vue';
import NavTopBar from './NavTopBar.vue';
import NavBottomBar from './NavBottomBar.vue';
import NavMobileMenu from './NavMobileMenu.vue';
import NavProfileDialog from './NavProfileDialog.vue';
import NavReleaseNotesDialog from './NavReleaseNotesDialog.vue';
import NavSupportDialog from './NavSupportDialog.vue';
import NavDraftCartsDialog from './NavDraftCartsDialog.vue';
import NavImageDialog from './NavImageDialog.vue';
import NavConfirmDialog from './NavConfirmDialog.vue';
import NavLegacyDialogs from './NavLegacyDialogs.vue';
import NavOfflineBlocker from './NavOfflineBlocker.vue';
import { NAV_PANELS } from './navPanels';
import { useAppChrome } from '@/composables/useAppChrome';
import { closeChromeDialogs, openChromeDialog, useChromeDialogs } from '@/composables/useChromeDialogs';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { clearLegacyAppCache, installExtensionUpdate, installUpdate, logout, openHelp, openLegacyWidgetSettings, openMenuItem, openPointsHistory, openPortal, openWidgetSettings, refreshLegacyNav, synchronize, toggleOffline } from '@/iframe/legacyNavActions';

const route = useRoute();
const { nav, visible, onApp, desktop, canHover, menu, activeView, panels, color } = useAppChrome();

const drawerOpen = ref(false);
const mobileMenuOpen = ref(false);
const activePanel = ref(null);
const sheetPanel = ref(null);
const dialogs = useChromeDialogs();
const confirm = useConfirmAction();
const onConfirm = (value) => confirm.run?.(value);
const onCancel = () => confirm.cancel?.();

const versionLabel = computed(() => (nav.version ? `v${nav.version}` : ''));

const ACTIONS = {
	profile: () => openChromeDialog('profile'),
	releaseNotes: () => openChromeDialog('releaseNotes'),
	support: () => openChromeDialog('support'),
	pointsHistory: openPointsHistory,
	widgetSettings: openWidgetSettings,
	legacyWidgetSettings: openLegacyWidgetSettings,
	synchronize,
	toggleOffline,
	clearLegacyAppCache,
	installUpdate,
	extensionUpdate: installExtensionUpdate,
	help: () => openHelp(),
	portal: openPortal,
	logout,
};

const run = (action) => ACTIONS[action]?.();

const openItem = (item) => (item?.shortcut === 'draft' ? openChromeDialog('draftCarts') : openMenuItem(item));

const toggleMobile = (key) => {
	if (key === 'menu') {
		activePanel.value = null;
		mobileMenuOpen.value = !mobileMenuOpen.value;
		return;
	}
	mobileMenuOpen.value = false;
	activePanel.value = activePanel.value === key ? null : key;
};

const onSheetToggle = (value) => {
	if (!value) activePanel.value = null;
};

const closeAll = () => {
	activePanel.value = null;
	mobileMenuOpen.value = false;
	drawerOpen.value = false;
};

watch(() => route.fullPath, closeAll);
watch([visible, desktop], closeAll);
watch(visible, (value) => !value && (closeChromeDialogs(), (confirm.open = false)));
watch(activePanel, (key) => {
	if (!key) return;
	sheetPanel.value = key;
	refreshLegacyNav();
});
watch(panels, (keys) => {
	if (activePanel.value && !keys.includes(activePanel.value)) activePanel.value = null;
});
watch(
	() => nav.requestedDialog,
	(dialog) => {
		if (!dialog) return;
		nav.requestedDialog = null;
		run(dialog);
	}
);
</script>
